import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, CheckCircle2, CalendarPlus, ListTodo, UserRound, AlertTriangle, Hash, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const Section = ({ icon: Icon, title, children, testid }) => (
  <div className="klt-glow-card rounded-3xl p-5" data-testid={testid}>
    <p className="font-heading font-bold flex items-center gap-2 mb-3"><Icon className="w-4 h-4 text-[#60A5FA]" /> {title}</p>
    {children}
  </div>
);

const Bullets = ({ items }) => <ul className="space-y-1.5 text-sm">{items.map((s, i) => <li key={i} className="flex gap-2"><span className="text-[#60A5FA]">•</span><span>{String(s)}</span></li>)}</ul>;

function Pick({ checked, onChange, title, sub, testid }) {
  return (
    <label className={`flex items-start gap-3 rounded-2xl p-3 cursor-pointer transition-colors ${checked ? "bg-[#3B82F6]/12" : "bg-secondary/40"}`} data-testid={testid}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 w-4 h-4 accent-[#3B82F6]" />
      <span className="min-w-0"><span className="font-semibold text-sm block">{title}</span>{sub && <span className="text-xs text-muted-foreground">{sub}</span>}</span>
    </label>
  );
}

const fmtDate = (s) => (s ? new Date(s).toLocaleString("fr-FR", { weekday: "short", day: "numeric", month: "short", hour: s.length > 10 ? "2-digit" : undefined, minute: s.length > 10 ? "2-digit" : undefined }) : "Sans échéance");

export default function MeetingReportPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [sel, setSel] = useState({ tasks: {}, appointments: {}, leadUpdates: {} });
  const [saving, setSaving] = useState(false);
  const [showTr, setShowTr] = useState(false);

  useEffect(() => {
    let t;
    const load = () => api.get(`/meetings/${id}`).then(({ data }) => {
      setM(data);
      if (data.status === "processing") t = setTimeout(load, 2500);
      else if (data.report) {
        const all = (k) => Object.fromEntries((data.report[k] || []).map((_, i) => [i, true]));
        setSel({ tasks: all("tasks"), appointments: all("appointments"), leadUpdates: all("leadUpdates") });
      }
    }).catch(() => { toast.error("Compte rendu introuvable"); navigate("/reunions"); });
    load();
    return () => clearTimeout(t);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (k, i, v) => setSel((s) => ({ ...s, [k]: { ...s[k], [i]: v } }));
  const picked = (k) => Object.entries(sel[k]).filter(([, v]) => v).map(([i]) => Number(i));
  const apply = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/meetings/${id}/apply`, { tasks: picked("tasks"), appointments: picked("appointments"), leadUpdates: picked("leadUpdates") });
      toast.success(`Validé : ${data.tasks} tâche(s), ${data.appointments} RDV, ${data.leadsUpdated} fiche(s) mise(s) à jour, ${data.leadsCreated} créée(s)`);
      window.dispatchEvent(new CustomEvent("birdie:changed"));
      setM((x) => ({ ...x, status: "applied", applied: data }));
    } catch (e) { toast.error(e.response?.data?.detail || "Validation impossible"); } finally { setSaving(false); }
  };
  const remove = async () => { await api.delete(`/meetings/${id}`); toast.success("Compte rendu supprimé"); navigate("/reunions"); };

  if (!m) return null;
  const rep = m.report || {};
  const done = m.status === "applied";
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="meeting-report-page">
      <button onClick={() => navigate("/reunions")} className="text-sm text-muted-foreground inline-flex items-center gap-1" data-testid="meeting-back"><ArrowLeft className="w-4 h-4" /> Comptes rendus</button>
      {m.status === "processing" && (
        <div className="klt-glow-card rounded-3xl p-8 text-center" data-testid="meeting-processing">
          <div className="mx-auto w-24 h-24 rounded-full klt-fab-neon klt-ai-halo flex items-center justify-center"><Sparkles className="w-10 h-10 text-white klt-sparkle" /></div>
          <p className="font-heading text-xl font-extrabold mt-5">L'IA rédige votre compte rendu…</p>
          <p className="text-sm text-muted-foreground mt-1">{m.segmentsDone < m.segmentsTotal ? `Transcription ${m.segmentsDone}/${m.segmentsTotal}` : "Analyse, décisions, tâches et RDV"}</p>
        </div>
      )}
      {m.status === "error" && <div className="klt-glow-card rounded-3xl p-6 text-rose-400" data-testid="meeting-error">{m.error || "Erreur de traitement"}</div>}
      {m.report && (
        <>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#60A5FA]">{m.kind === "call" ? "Appel" : "Réunion"} · {new Date(m.createdAt).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}{m.durationSec ? ` · ${Math.round(m.durationSec / 60)} min` : ""} · {m.ownerName}</p>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold mt-1" data-testid="meeting-title">{rep.title}</h1>
          </div>
          <Section icon={Sparkles} title="L'essentiel" testid="meeting-summary"><Bullets items={rep.summary || []} /></Section>
          {(rep.decisions?.length > 0 || rep.keyFigures?.length > 0) && (
            <div className="grid sm:grid-cols-2 gap-4">
              {rep.decisions?.length > 0 && <Section icon={CheckCircle2} title="Décisions" testid="meeting-decisions"><Bullets items={rep.decisions} /></Section>}
              {rep.keyFigures?.length > 0 && <Section icon={Hash} title="Chiffres clés" testid="meeting-figures"><Bullets items={rep.keyFigures} /></Section>}
            </div>
          )}
          {rep.risks?.length > 0 && <Section icon={AlertTriangle} title="Risques & objections" testid="meeting-risks"><Bullets items={rep.risks} /></Section>}

          {[["leadUpdates", UserRound, "Fiches prospects / clients", (u) => [u.label || [u.firstName, u.lastName].filter(Boolean).join(" ") + (u.company ? ` (${u.company})` : ""), `${u.leadId ? "Mise à jour" : "Nouvelle fiche"} · ${u.update || ""}${u.conversionScore != null ? ` · Signature ${u.conversionScore} %` : ""}`]],
            ["tasks", ListTodo, "Tâches", (t) => [t.title, fmtDate(t.dueDate)]],
            ["appointments", CalendarPlus, "RDV au planning", (a) => [a.title, `${fmtDate(a.startAt)}${a.contactName ? ` · ${a.contactName}` : ""}`]]].map(([k, Icon, title, fn]) => (rep[k]?.length > 0 && (
            <Section key={k} icon={Icon} title={title} testid={`meeting-${k}`}>
              <div className="space-y-2">{rep[k].map((x, i) => { const [t, s] = fn(x); return <Pick key={i} checked={!!sel[k][i]} onChange={(v) => toggle(k, i, v)} title={t} sub={s} testid={`pick-${k}-${i}`} />; })}</div>
            </Section>
          )))}

          {done ? (
            <div className="rounded-2xl bg-emerald-500/12 text-emerald-400 p-4 text-sm font-semibold flex items-center gap-2" data-testid="meeting-applied"><CheckCircle2 className="w-5 h-5" /> Validé : planning et fiches mis à jour</div>
          ) : (
            <Button onClick={apply} disabled={saving} data-testid="meeting-apply-btn" className="w-full h-13 py-3.5 rounded-2xl klt-fab-neon font-semibold text-base">Valider et mettre à jour planning & fiches</Button>
          )}
          <div className="flex items-center justify-between">
            <button onClick={() => setShowTr((v) => !v)} data-testid="meeting-transcript-toggle" className="text-sm text-[#60A5FA] font-semibold">{showTr ? "Masquer" : "Voir"} la transcription</button>
            <button onClick={remove} data-testid="meeting-delete" className="text-sm text-rose-400 inline-flex items-center gap-1"><Trash2 className="w-4 h-4" /> Supprimer</button>
          </div>
          {showTr && <p className="text-sm text-muted-foreground whitespace-pre-wrap klt-glow-card rounded-2xl p-4" data-testid="meeting-transcript">{m.transcript}</p>}
        </>
      )}
    </div>
  );
}
